import { defineStore } from 'pinia';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

function apiUrl(path) {
  return `${apiProxyUrl || ''}/api/creator-webinars${path}`;
}

async function readPayload(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.message || payload?.error || `Webinar request failed (${response.status})`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

export const useWebinarStore = defineStore('webinars', {
  state: () => ({
    eventsByProduct: {},
    loadingProductIds: {},
    loadedProductIds: {},
    saving: false,
    deletingEventIds: {},
    registrationsByEvent: {},
    loadingRegistrationsEventIds: {},
    error: '',
  }),

  getters: {
    eventsForProduct: (state) => (featureInstanceId) => (
      state.eventsByProduct[String(featureInstanceId)] || []
    ),
    isLoadingProduct: (state) => (featureInstanceId) => Boolean(
      state.loadingProductIds[String(featureInstanceId)]
    ),
  },

  actions: {
    setProductEvents(featureInstanceId, events) {
      this.eventsByProduct = {
        ...this.eventsByProduct,
        [String(featureInstanceId)]: Array.isArray(events) ? events : [],
      };
    },

    async fetchEvents(featureInstanceId, { force = false, silent = false } = {}) {
      const key = String(featureInstanceId || '');
      if (!key) return [];
      if (!force && this.loadedProductIds[key]) return this.eventsForProduct(key);
      if (this.loadingProductIds[key]) return this.eventsForProduct(key);

      this.error = '';
      if (!silent) this.loadingProductIds = { ...this.loadingProductIds, [key]: true };
      try {
        const query = new URLSearchParams({ featureInstanceId: key });
        const response = await fetch(apiUrl(`/events?${query.toString()}`), {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        const payload = await readPayload(response);
        const events = Array.isArray(payload?.events) ? payload.events : [];
        this.setProductEvents(key, events);
        this.loadedProductIds = { ...this.loadedProductIds, [key]: true };
        return events;
      } catch (error) {
        this.error = String(error?.message || 'Failed to load Webinar events');
        throw error;
      } finally {
        if (!silent) this.loadingProductIds = { ...this.loadingProductIds, [key]: false };
      }
    },

    async createEvent(input) {
      this.saving = true;
      this.error = '';
      try {
        const response = await fetch(apiUrl('/events'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input || {}),
        });
        const payload = await readPayload(response);
        const event = payload?.event;
        if (event?.featureInstanceId) {
          const key = String(event.featureInstanceId);
          const current = this.eventsForProduct(key).filter((row) => Number(row.eventId) !== Number(event.eventId));
          this.setProductEvents(key, [event, ...current]);
          this.loadedProductIds = { ...this.loadedProductIds, [key]: true };
        }
        return event;
      } catch (error) {
        this.error = String(error?.message || 'Failed to create Webinar event');
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async updateEvent(eventId, input) {
      this.saving = true;
      this.error = '';
      try {
        const response = await fetch(apiUrl(`/events/${encodeURIComponent(eventId)}`), {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input || {}),
        });
        const payload = await readPayload(response);
        const event = payload?.event;
        if (event?.featureInstanceId) {
          Object.keys(this.eventsByProduct).forEach((key) => {
            this.setProductEvents(key, this.eventsForProduct(key).filter((row) => Number(row.eventId) !== Number(eventId)));
          });
          const key = String(event.featureInstanceId);
          this.setProductEvents(key, [event, ...this.eventsForProduct(key)]);
        }
        return event;
      } catch (error) {
        this.error = String(error?.message || 'Failed to update Webinar event');
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async deleteEvent(eventId, featureInstanceId) {
      const eventKey = String(eventId || '');
      this.error = '';
      this.deletingEventIds = { ...this.deletingEventIds, [eventKey]: true };
      try {
        const response = await fetch(apiUrl(`/events/${encodeURIComponent(eventId)}`), {
          method: 'DELETE',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        await readPayload(response);
        const productKey = String(featureInstanceId || '');
        if (productKey) {
          this.setProductEvents(productKey, this.eventsForProduct(productKey).filter((row) => Number(row.eventId) !== Number(eventId)));
        }
      } catch (error) {
        this.error = String(error?.message || 'Failed to delete Webinar event');
        throw error;
      } finally {
        this.deletingEventIds = { ...this.deletingEventIds, [eventKey]: false };
      }
    },

    async fetchRegistrations(eventId, { force = false } = {}) {
      const key = String(eventId || '').trim();
      if (!key) throw new Error('Event id is required');
      if (!force && this.registrationsByEvent[key]) return this.registrationsByEvent[key];
      if (this.loadingRegistrationsEventIds[key]) return this.registrationsByEvent[key] || null;
      this.loadingRegistrationsEventIds = { ...this.loadingRegistrationsEventIds, [key]: true };
      try {
        const response = await fetch(apiUrl(`/events/${encodeURIComponent(key)}/registrations`), {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        const payload = await readPayload(response);
        this.registrationsByEvent = { ...this.registrationsByEvent, [key]: payload };
        return payload;
      } finally {
        this.loadingRegistrationsEventIds = { ...this.loadingRegistrationsEventIds, [key]: false };
      }
    },
  },
});
